import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import {
  useHasConsentBanner,
  useSiteBannerMessage,
} from 'src/app/nav-system/banner/hooks';
import { useMarketingBanner } from 'src/app/nav-system/banner/marketing-banner';
import { getUserGlobalTheme } from 'src/selectors/global-selectors';

import { useRovoChatContext } from '../nav-system';

export type LayoutVisibility = {
  banner: boolean;
  aside: boolean;
  panel: boolean;
};

/**
 * Hook to determine visibility of layout areas based on context and user state
 *
 * Layout slot visibility rules:
 * - banner: conditional - if site banner or consent banner has content (height will be calc in slot component)
 * - topNav: always - some items are conditional based on auth
 * - sideNav: always - content changes based on context, navigation-system controls expand/collapse
 * - main: always - primary content area
 * - aside: conditional - shown when context requires sidebar (e.g., commit page, repo source)
 * - panel: conditional - shown when user has rovo chat open
 *
 * @returns LayoutVisibility object indicating which layout slots should render
 */
export const useLayoutVisibility = (): LayoutVisibility => {
  const { chatState } = useRovoChatContext();
  const hasConsentBanner = useHasConsentBanner();
  const hasSiteMessageBanner = Boolean(useSiteBannerMessage());
  const { shouldDisplayMarketingBanner } = useMarketingBanner();

  const panelState =
    chatState.isEnabled && !!chatState.siteId && chatState.isActive;

  return useMemo(
    () => ({
      banner:
        hasConsentBanner ||
        hasSiteMessageBanner ||
        shouldDisplayMarketingBanner,
      aside: false, // TODO: implement sidebar visibility logic based on route/context (COREX-12212)
      panel: panelState,
    }),
    [
      hasConsentBanner,
      hasSiteMessageBanner,
      panelState,
      shouldDisplayMarketingBanner,
    ]
  );
};

// this gives us the color mode saved in user prefs for the current user. we use this
// to set the intitial theme. note: we don't keep redux up to date when switching
// the theme so this is only for initial page load. As the user switches we track
// that using the app provider hooks (and also write their choice to the DB to have
// on next full page load)
export function useInitThemeColor() {
  const initialGlobalTheme = useSelector(getUserGlobalTheme);

  return initialGlobalTheme;
}
